"use client";

import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface AutocompleteComboboxProps<T> {
  items: T[];
  onSelect?: (item: T | null) => void;
  onSearchChange?: (searchTerm: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  getItemKey: (item: T) => string | number;
  getItemLabel: (item: T) => string;
  renderItem?: (item: T) => React.ReactNode;
  searchable?: boolean;
  clearable?: boolean;
  defaultValue?: T;
}

export default function AutocompleteCombobox<T>({
  items,
  onSelect,
  onSearchChange,
  placeholder = "Search items...",
  emptyMessage = "No items found.",
  className,
  getItemKey,
  getItemLabel,
  renderItem,
  searchable = true,
  clearable = true,
  defaultValue,
}: AutocompleteComboboxProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState<T | null>(
    defaultValue || null
  );
  const [inputValue, setInputValue] = React.useState(() =>
    defaultValue ? getItemLabel(defaultValue) : ""
  );

  const handleSelect = (item: T) => {
    setSelectedItem(item);
    setInputValue(getItemLabel(item));
    setOpen(false);
    onSelect?.(item);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItem(null);
    setInputValue("");
    onSelect?.(null);
  };

  const handleInputChange = (newValue: string) => {
    setInputValue(newValue);
    onSearchChange?.(newValue);

    // If the input doesn't match any item exactly, clear the selected item
    const exactMatch = items.find(
      (item) => getItemLabel(item).toLowerCase() === newValue.toLowerCase()
    );
    if (!exactMatch && selectedItem) {
      setSelectedItem(null);
    } else if (exactMatch) {
      setSelectedItem(exactMatch);
    }
  };

  const filteredItems = searchable
    ? items.filter((item) =>
        getItemLabel(item).toLowerCase().includes(inputValue.toLowerCase())
      )
    : items;

  const displayValue = selectedItem ? getItemLabel(selectedItem) : inputValue;

  return (
    <div className={cn("w-full max-w-sm", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between relative font-normal"
          >
            <span className="truncate text-left flex-1 capitalize">
              {displayValue || placeholder}
            </span>
            <div className="flex items-center gap-1">
              {clearable && selectedItem && (
                <X
                  className="h-4 w-4 shrink-0 opacity-50 hover:opacity-100"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            {searchable && (
              <CommandInput
                placeholder={placeholder}
                value={inputValue}
                onValueChange={handleInputChange}
              />
            )}
            <CommandList>
              <CommandEmpty>{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {filteredItems.map((item) => (
                  <CommandItem
                    key={getItemKey(item)}
                    value={String(getItemKey(item))}
                    onSelect={() => handleSelect(item)}
                    className="cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedItem &&
                          getItemKey(selectedItem) === getItemKey(item)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    <div className="flex-1">
                      {renderItem ? renderItem(item) : getItemLabel(item)}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
